import { usePermission } from "@/hooks/usePermission";
import { createCheckoutSession } from "@/api/client";
import { useState, type ReactNode } from "react";
import { Lock } from "lucide-react";

interface GatedFeatureProps {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function GatedFeature({ feature, children, fallback }: GatedFeatureProps) {
  const { allowed, requiredTier } = usePermission(feature);
  const [loading, setLoading] = useState(false);

  if (allowed) return <>{children}</>;

  if (fallback) return <>{fallback}</>;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await createCheckoutSession(requiredTier);
      window.location.href = checkout_url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="card relative overflow-hidden">
      {/* Blurred content placeholder */}
      <div className="pointer-events-none select-none blur-[6px] opacity-30 p-6">
        <div className="h-32 rounded-ds-sm bg-panel-3/50" />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-bg-2/60 backdrop-blur-sm">
        <div className="flex items-center gap-2 rounded-full border border-line bg-panel-3/80 px-3 py-1.5">
          <Lock className="h-3.5 w-3.5 text-ink-3" />
          <span className="text-xs font-medium uppercase tracking-wider text-ink-2">
            {requiredTier}
          </span>
        </div>
        <p className="text-sm text-ink-3">
          This feature requires the {requiredTier} plan
        </p>
        <button
          onClick={handleUpgrade}
          disabled={loading}
          className="btn btn--accent px-4 py-1.5 text-sm font-medium"
        >
          {loading ? "Loading..." : `Upgrade to ${requiredTier}`}
        </button>
      </div>
    </div>
  );
}

export function TierBadge({ feature }: { feature: string }) {
  const { allowed, requiredTier } = usePermission(feature);
  if (allowed) return null;

  const colors: Record<string, string> = {
    explorer: "bg-accent/10 text-accent border-accent/20",
    builder: "bg-stage-stone/10 text-stage-stone border-stage-stone/20",
    scale: "bg-warn/10 text-warn border-warn/20",
    enterprise: "bg-ok/10 text-ok border-ok/20",
  };

  return (
    <span className={`rounded-md border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${colors[requiredTier] ?? "bg-ink-3/10 text-ink-3 border-ink-3/20"}`}>
      {requiredTier}
    </span>
  );
}
